import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BaseAuthenticationBloc } from '@kauth';
import { SubscriptionBloc, TagBloc } from '@kbloc';
import { DataStoreService } from '@kservice';
import { Status } from '@ktypes/models';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PremiumGateService {
  constructor(
    private _injector: Injector,
    private _router: Router,
    private _subscriptionBloc: SubscriptionBloc,
    private _tagBloc: TagBloc,
    private _dataStoreService: DataStoreService
  ) {}

  private _lastStatusFetchAt = 0;
  private _lastPremiumUnlockAt: number | null = null;
  private _startupPaywallFired = false;
  private _onboardingCompletedForPaywall = false;
  private readonly _postUpgradeHydrationWindowMs = 2 * 60 * 1000;

  markPremiumUnlockedNow(): void {
    this._lastPremiumUnlockAt = Date.now();
  }

  markOnboardingCompletedForPaywall(): void {
    this._onboardingCompletedForPaywall = true;
  }

  consumeOnboardingCompletedForPaywall(): boolean {
    const value = this._onboardingCompletedForPaywall;
    this._onboardingCompletedForPaywall = false;
    return value;
  }

  shouldHydrateCardsAfterUpgrade(): boolean {
    if (this._lastPremiumUnlockAt == null) {
      return false;
    }
    return Date.now() - this._lastPremiumUnlockAt <= this._postUpgradeHydrationWindowMs;
  }

  private _ensureStatusRequested() {
    // Ensure we have a user id; otherwise, wait until auth data is ready.
    if (!this._dataStoreService?.authData?.user?.id) {
      this._dataStoreService.authData$
        .pipe(
          filter((authData) => !!authData?.user?.id),
          take(1)
        )
        .subscribe(() => this._ensureStatusRequested());
      return;
    }
    const now = Date.now();
    // Throttle status fetches to avoid rapid loops that can flicker UI.
    if (now - this._lastStatusFetchAt < 5000) return;
    this._lastStatusFetchAt = now;
    this._subscriptionBloc.getSubscriptionStatus();
  }

  private _eligibleOfferingId(): string | null {
    const subStatus = this._subscriptionBloc.subscriptionStatus?.data;
    if (subStatus && !subStatus.canonicalHasAccess && subStatus.canonicalCanPurchase) {
      return subStatus.canonicalOfferingId ?? null;
    }
    return null;
  }

  private _getAuthBloc(): BaseAuthenticationBloc | null {
    try {
      return this._injector.get(BaseAuthenticationBloc);
    } catch {
      return null;
    }
  }

  /**
   * Best-effort premium gate:
   * - Uses cached subscription status (kicks off a refresh but does not await it)
   * - If eligible freemium, stores offeringId + returnUrl and signals the UI to show the paywall
   * - Otherwise returns false and caller can proceed (402 handler remains a safety net)
   */
  showPaywallIfEligible(returnUrl: string, dismissible = true): boolean {
    this._ensureStatusRequested();
    const offeringId = this._eligibleOfferingId();
    if (offeringId) {
      this._getAuthBloc()?.setPremiumPaywallContext(offeringId, returnUrl, dismissible);
      return true;
    }
    return false;
  }

  /**
   * Best-effort: returns the offering id if the user is eligible freemium.
   * Useful when you want to *render* a promo (not redirect).
   */
  getEligibleOfferingId(): string | null {
    this._ensureStatusRequested();
    return this._eligibleOfferingId();
  }

  /**
   * Best-effort async variant for views that render after subscription status resolves.
   * Returns once status is done/error so callers can reliably render premium placeholders.
   */
  getEligibleOfferingIdAsync(): Observable<string | null> {
    this._ensureStatusRequested();
    return this._subscriptionBloc.subscriptionStatus$.pipe(
      filter((status) => status?.status === Status.done || status?.status === Status.error),
      take(1),
      map(() => this._eligibleOfferingId())
    );
  }

  showPaywallIfEligibleAsync(returnUrl: string): Observable<boolean> {
    this._ensureStatusRequested();
    return this._subscriptionBloc.subscriptionStatus$.pipe(
      filter((status) => status?.status === Status.done || status?.status === Status.error),
      take(1),
      map((status) => {
        if (status?.status !== Status.done) {
          return false;
        }

        if (status?.data?.canonicalHasAccess === true) {
          return true;
        }

        this.showPaywallIfEligible(returnUrl);
        return false;
      })
    );
  }

  // Note: use showPaywallIfEligibleAsync when you need to await status.

  /**
   * Called once on the main screen (e.g. Today) to present the startup paywall.
   *
   * - Hard paywall users (tag): non-dismissible paywall.
   * - Soft paywall (just completed onboarding): dismissible paywall.
   *
   * Returns an Observable that emits true if a paywall was shown, false otherwise.
   */
  checkStartupPaywall(): Observable<boolean> {
    if (this._startupPaywallFired) return of(false);
    this._startupPaywallFired = true;

    const justCompletedOnboarding = this.consumeOnboardingCompletedForPaywall();

    return this._tagBloc.hardPaywallEnabled$.pipe(
      take(1),
      switchMap((isHardPaywall) => {
        if (!isHardPaywall && !justCompletedOnboarding) return of(false);

        return this.getEligibleOfferingIdAsync().pipe(
          map((offeringId) => {
            if (!offeringId) return false;

            const dismissible = !isHardPaywall;
            this._getAuthBloc()?.setPremiumPaywallContext(offeringId, '/today', dismissible);
            return true;
          })
        );
      })
    );
  }

  resetStartupPaywallFlag(): void {
    this._startupPaywallFired = false;
  }

  /**
   * Re-present the hard paywall if the user's tag demands it but the overlay is
   * not currently visible (e.g. after returning to the tab).
   */
  reEnforceHardPaywallIfNeeded(): void {
    const url = this._router.url;
    if (
      url.startsWith('/welcome') ||
      url.startsWith('/login') ||
      url.startsWith('/dialogue') ||
      url.startsWith('/question_set')
    )
      return;

    const authBloc = this._getAuthBloc();
    if (!authBloc) return;

    this._tagBloc.hardPaywallEnabled$.pipe(take(1)).subscribe((isHardPaywall) => {
      if (!isHardPaywall) return;

      this.getEligibleOfferingIdAsync().subscribe((offeringId) => {
        if (offeringId) {
          authBloc.setPremiumPaywallContext(offeringId, '/today', false);
        }
      });
    });
  }
}
